
import AssessorOnboardingBasics from './AssessorOnboardingBasics.vue';
import AssessorOnboardingExpertise from './AssessorOnboardingExpertise.vue';
import AssessorOnboardingBusinessConditions from './AssessorOnboardingBusinessConditions.vue';
import AssessorOnboardingCompanyDetails from './AssessorOnboardingCompanyDetails.vue';
import requestAPI from '@/logic/requestBeApi';
import router from '@/router';
import helpers from '@/logic/helpers/forms/onboardingHelpers';
import EazyHeading from '@/components/EazyHeading.vue';

export default {
  name: 'AssessorOnboarding',

  components: {
    AssessorOnboardingBasics,
    AssessorOnboardingExpertise,
    AssessorOnboardingBusinessConditions,
    AssessorOnboardingCompanyDetails,
    EazyHeading,
  },

  data(): Record<string, unknown> {
    return {
      displaySkipInfoAlert: false,

      onboardingStep: '',
      processingGetStateRequest: true,
      completedSteps: [],
    };
  },

  computed: {
    onStepTwo(): boolean {
      return this.onboardingStep === 'expertise' || this.onboardingStep === 'business_conditions';
    },

    stepTwoHalfCompleted(): boolean {
      return (
        (this.completedSteps.includes(4) || this.completedSteps.includes(5)) &&
        !this.stepTwoCompleted
      );
    },

    stepTwoCompleted(): boolean {
      return this.completedSteps.includes(4) && this.completedSteps.includes(5);
    },
  },

  mounted(): void {
    this.decideWhichStepBasedOnOnboardingStatus();
    this.addLogicForLeavingOnboarding();
  },

  beforeDestroy(): void {
    window.removeEventListener('popstate', this.actionOnLeavingOnboardingAttempt);
  },

  methods: {
    async setStep(
      step: string,
      previousScreenResponse: { json: () => any },
      needsMakeRequestForStatus: boolean,
    ): Promise<void> {
      if (previousScreenResponse) {
        const data = await previousScreenResponse.json();
        this.completedSteps = data.onboarding_states_completed;
      }

      if (needsMakeRequestForStatus) {
        const userEmail = localStorage.getItem('email');
        const resp = await requestAPI({
          method: 'GET',
          url: `/api/v1/onboard/state/${userEmail}`,
        });

        if (resp.ok) {
          const data = await resp.json();
          this.completedSteps = data.onboarding_states_completed;
        }
      }

      if (step === 'complete to home') {
        helpers.endOnboarding();
        this.$message.success(this.$t('youHaveBeenOnboarded'));
      } else {
        this.setProperNextStepConsideringCompletedOnes(step);

        setTimeout(() => {
          window.scrollTo(0, 0);
        }, 300);
      }
    },

    setProperNextStepConsideringCompletedOnes(step: string): void {
      if (step === 'basics') {
        const stepHasBeenAlreadyCompleted = this.completedSteps.includes(3);
        stepHasBeenAlreadyCompleted
          ? this.setProperNextStepConsideringCompletedOnes('expertise')
          : (this.onboardingStep = step);
      } else if (step === 'expertise') {
        const stepHasBeenAlreadyCompleted = this.completedSteps.includes(4);
        stepHasBeenAlreadyCompleted
          ? this.setProperNextStepConsideringCompletedOnes('business_conditions')
          : (this.onboardingStep = step);
      } else if (step === 'business_conditions') {
        const stepHasBeenAlreadyCompleted = this.completedSteps.includes(5);
        stepHasBeenAlreadyCompleted
          ? this.setProperNextStepConsideringCompletedOnes('company')
          : (this.onboardingStep = step);
      } else if (step === 'company') {
        const stepHasBeenAlreadyCompleted = this.completedSteps.includes(8);
        stepHasBeenAlreadyCompleted
          ? this.setProperNextStepConsideringCompletedOnes('complete to home')
          : (this.onboardingStep = step);
      } else if (step === 'complete to home') {
        helpers.endOnboarding();
        this.$message.success(this.$t('youHaveBeenOnboarded'));
      }
    },

    async decideWhichStepBasedOnOnboardingStatus(): Promise<void> {
      const userEmail = localStorage.getItem('email');

      if (!userEmail) return;

      this.processingGetStateRequest = true;

      const resp = await requestAPI({
        method: 'GET',
        url: `/api/v1/onboard/state/${userEmail}`,
      });

      if (resp.ok) {
        const data = await resp.json();
        this.completedSteps = data.onboarding_states_completed;

        if (!data.onboarding_next_step_uri && data.onboarding_state !== 8) {
          helpers.endOnboarding();
          this.processingGetStateRequest = false;
          return;
        }

        // it returns onboarding_state of completed screen, so
        // we have to navigate to the next one
        if (data.onboarding_state === 2) {
          this.onboardingStep = 'basics';
        } else if (data.onboarding_state === 3) {
          this.onboardingStep = 'expertise';
        } else if (data.onboarding_state === 4) {
          this.onboardingStep = 'business_conditions';
        } else if (data.onboarding_state === 5) {
          this.onboardingStep = 'company';
        } else if (data.onboarding_state === 8) {
          helpers.endOnboarding();
          this.$message.success(this.$t('youHaveBeenOnboarded'));
        }
      } else if (resp.status !== 401) {
        this.$message.error(this.$t('someErrorOccurredOnboarding'));
      }

      this.processingGetStateRequest = false;
    },

    actionOnLeavingOnboardingAttempt(): void {
      if (this.$route.path !== '/assessor-onboarding') return;

      this.$confirm(this.$t('leavingOnboardingNotRecommendedInfo'), this.$t('warning'), {
        confirmButtonText: 'OK',
        cancelButtonText: this.$t('cancel'),
        type: 'warning',
      })
        .then(() => {
          router.push('/login');
        })
        .catch(() => {
          history.pushState(null, document.URL, location.href);
        });
    },

    addLogicForLeavingOnboarding(): void {
      history.pushState(null, document.URL, location.href);
      window.addEventListener('popstate', this.actionOnLeavingOnboardingAttempt);
    },
  },
};
